/**
 * RecentSearchesSection — reusable recent-search chips for GlobalSearch.
 *
 * Shown at the top of the empty state and again on no-results so operators
 * can re-run a prior query without scrolling past Quick Navigation.
 */

import { Clock } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface RecentSearchesSectionProps {
  recentSearches: string[]
  onSelect: (query: string) => void
  /** When set, only show recents whose text includes this (case-insensitive). */
  filterQuery?: string
  maxItems?: number
  /** When true, render the section even with zero matches (empty hint). */
  alwaysShow?: boolean
  className?: string
}

export function RecentSearchesSection({
  recentSearches,
  onSelect,
  filterQuery,
  maxItems = 8,
  alwaysShow = false,
  className,
}: RecentSearchesSectionProps) {
  const normalizedFilter = filterQuery?.trim().toLowerCase() ?? ''
  const visible = normalizedFilter
    ? recentSearches.filter((s) => s.toLowerCase().includes(normalizedFilter))
    : recentSearches

  const items = visible.slice(0, maxItems)

  if (!alwaysShow && items.length === 0) return null

  return (
    <div className={cn('mb-4', className)}>
      <div className="px-1 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
        Recent Searches
      </div>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map((search) => (
            <button
              key={search}
              type="button"
              onClick={() => onSelect(search)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-colors',
                'bg-muted/60 hover:bg-muted',
                'text-muted-foreground',
              )}
            >
              <Clock className="w-3 h-3 flex-shrink-0" />
              {search}
            </button>
          ))}
        </div>
      ) : (
        <p className="px-1 text-xs text-muted-foreground">
          {normalizedFilter
            ? 'No recent searches match your query.'
            : 'Search for jobs, emails, or cards — your recent queries will appear here.'}
        </p>
      )}
    </div>
  )
}
